import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Team', path: '/team' },
    { name: 'Why Us', path: '/why-choose-us' },
    { name: 'Quick Start', path: '/quick-start' },
    { name: 'Contact', path: '/contact' },
  ];

  const isTransparent = isHomePage && !isScrolled;

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isTransparent 
          ? 'bg-black/20 backdrop-blur-sm' 
          : 'bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo-link">
            <motion.div 
              className="bg-white rounded-xl px-3 py-2"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_samrelocation/artifacts/ezz0kk2s_image.png" 
                alt="SAM Relocation Group" 
                className="h-8 w-auto"
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-medium transition-all rounded-full ${
                  location.pathname === link.path
                    ? 'text-[#3498DB]'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.span 
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#3498DB]"
                    layoutId="activeNav"
                  />
                )}
              </Link>
            ))}
            
            <Link
              to="/contact"
              className="ml-4 px-5 py-2.5 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white text-sm font-semibold rounded-full transition-all duration-300 hover:shadow-[0_0_20px_rgba(52,152,219,0.4)] hover:scale-105"
              data-testid="nav-cta-button"
            >
              Book Now
            </Link>
          </div>

          {/* Contact Info - Desktop */}
          <div className="hidden xl:flex items-center gap-4 text-sm text-white/70">
            <a href="tel:+971563528126" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone size={14} />
              <span>+971 563 528 126</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0a0a0a]/98 backdrop-blur-xl border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 px-4 text-base font-medium rounded-xl transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#3498DB] bg-[#3498DB]/10'
                      : 'text-white/80 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              
              <div className="pt-4 space-y-3 border-t border-white/10 mt-4">
                <a href="tel:+971563528126" className="flex items-center gap-3 text-white/70 hover:text-white py-2">
                  <Phone size={16} />
                  <span>+971 563 528 126</span>
                </a>
                <a href="mailto:groupsamrelocate@gmail.com" className="flex items-center gap-3 text-white/70 hover:text-white py-2">
                  <Mail size={16} />
                  <span>groupsamrelocate@gmail.com</span>
                </a>
              </div>
              
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-center py-4 mt-4 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-semibold rounded-full"
                data-testid="mobile-nav-cta-button"
              >
                Book Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
